import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search, User, Truck, CornerDownLeft } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import DriverDetailModal from "@/components/DriverDetailModal";
import VehicleDetailModal from "@/components/VehicleDetailModal";
import { useAuth } from "@/contexts/AuthContext";

const MAX_PER_GROUP = 6;

export default function GlobalSearch() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Fahrer have no access to driver/vehicle master lists in the UI – keep the
  // simple "search trips" behaviour for them.
  const canQuickOpen = user.role !== "fahrer";

  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [drivers, setDrivers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [detailDriver, setDetailDriver] = useState(null);
  const [detailVehicle, setDetailVehicle] = useState(null);
  const boxRef = useRef(null);

  // Load master data once (only for roles that may open detail cards).
  useEffect(() => {
    if (!canQuickOpen) return;
    (async () => {
      try {
        const [d, v] = await Promise.all([api.get("/drivers"), api.get("/vehicles")]);
        setDrivers(d.data || []);
        setVehicles(v.data || []);
      } catch { /* ignore */ }
    })();
  }, [canQuickOpen]);

  // Close dropdown on outside click.
  useEffect(() => {
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, []);

  const q = query.trim().toLowerCase();

  const driverHits = useMemo(() => {
    if (!q) return [];
    return drivers
      .filter((d) => `${d.firstname} ${d.lastname} ${d.employee_number || ""} ${d.phone || ""} ${d.email || ""}`.toLowerCase().includes(q))
      .slice(0, MAX_PER_GROUP);
  }, [drivers, q]);

  const vehicleHits = useMemo(() => {
    if (!q) return [];
    return vehicles
      .filter((v) => `${v.license_plate} ${v.vehicle_type || ""}`.toLowerCase().includes(q))
      .slice(0, MAX_PER_GROUP);
  }, [vehicles, q]);

  const goTrips = () => {
    setOpen(false);
    navigate(query.trim() ? `/fahrten?q=${encodeURIComponent(query.trim())}` : "/fahrten");
  };

  const submit = (e) => {
    e.preventDefault();
    goTrips();
  };

  const openDriver = (d) => {
    setOpen(false);
    setQuery("");
    setDetailDriver(d);
  };
  const openVehicle = (v) => {
    setOpen(false);
    setQuery("");
    setDetailVehicle(v);
  };

  const hasResults = driverHits.length > 0 || vehicleHits.length > 0;
  const showPanel = open && canQuickOpen && q.length > 0;

  return (
    <>
      <div className="relative hidden sm:block" ref={boxRef}>
        <form onSubmit={submit} data-testid="global-search-form">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
          <Input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={canQuickOpen ? "Suche: Fahrer, Kennzeichen, Fahrten…" : "Fahrten suchen…"}
            className="pl-8 w-44 lg:w-72 h-9"
            data-testid="global-search-input"
            autoComplete="off"
          />
        </form>

        {showPanel && (
          <div
            className="absolute right-0 mt-2 w-[340px] max-h-[70vh] overflow-y-auto rounded-lg border border-border bg-popover shadow-xl z-50 p-1.5"
            data-testid="global-search-results"
          >
            {driverHits.length > 0 && (
              <div className="mb-1">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Fahrer</div>
                {driverHits.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => openDriver(d)}
                    className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-accent transition-colors"
                    data-testid={`global-search-driver-${d.id}`}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-blue-100 text-blue-700 dark:bg-blue-900/60 dark:text-blue-200">
                      <User className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{d.firstname} {d.lastname}</span>
                      <span className="block truncate text-xs text-muted-foreground">{d.employee_number || "—"}{d.phone ? ` · ${d.phone}` : ""}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {vehicleHits.length > 0 && (
              <div className="mb-1">
                <div className="px-2 py-1 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Fahrzeuge</div>
                {vehicleHits.map((v) => (
                  <button
                    key={v.id}
                    type="button"
                    onClick={() => openVehicle(v)}
                    className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-accent transition-colors"
                    data-testid={`global-search-vehicle-${v.id}`}
                  >
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-700 dark:bg-indigo-900/60 dark:text-indigo-200">
                      <Truck className="h-3.5 w-3.5" />
                    </span>
                    <span className="min-w-0">
                      <span className="block truncate font-medium">{v.license_plate || "Fahrzeug"}</span>
                      <span className="block truncate text-xs font-mono text-muted-foreground">{v.vehicle_type || "—"}</span>
                    </span>
                  </button>
                ))}
              </div>
            )}

            {!hasResults && (
              <div className="px-2 py-2 text-sm text-muted-foreground" data-testid="global-search-empty">
                Keine Fahrer/Fahrzeuge gefunden.
              </div>
            )}

            <div className="border-t border-border mt-1 pt-1">
              <button
                type="button"
                onClick={goTrips}
                className="w-full flex items-center gap-2 rounded-md px-2 py-1.5 text-sm text-left hover:bg-accent transition-colors"
                data-testid="global-search-trips"
              >
                <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-muted text-muted-foreground">
                  <Search className="h-3.5 w-3.5" />
                </span>
                <span className="min-w-0 truncate">Fahrten durchsuchen nach „{query.trim()}"</span>
                <CornerDownLeft className="h-3.5 w-3.5 ml-auto text-muted-foreground" />
              </button>
            </div>
          </div>
        )}
      </div>

      <DriverDetailModal driver={detailDriver} open={!!detailDriver} onOpenChange={(o) => !o && setDetailDriver(null)} />
      <VehicleDetailModal vehicle={detailVehicle} open={!!detailVehicle} onOpenChange={(o) => !o && setDetailVehicle(null)} />
    </>
  );
}
